import Guid from "_shared/guids/guid";
import _ from "lodash";
import Forum from "messaging/forums/forum";
import Topic from "messaging/forums/topics/topic";
import AHDate from "_shared/dates/date";
import Proposal from "work/proposals/proposal";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { Masked } from "work/_shared/masked-fields";
import EntityMember from "_shared/entity-members/entity-member";
import CommentThreadSchema, { CommentThreadJSON } from "work/proposals/comments/threads/_schemas/comment-thread-schema";
import ForumEntityClass from "notifications/values/forum-entity-class/forum-entity-class";
import ForumName from "notifications/values/forum-name/forum-name";

export enum Audience {
  VendorReviewers = "vendorReviewers",
  ClientReviewers = "clientReviewers",
  AllReviewers = "allReviewers",
}

export default class CommentThread {
  public static readonly EXAMPLE = new CommentThread({
    proposalId: Guid.generate(),
    field: ProposalField.Name,
    subscribers: [],
    isExternal: false,
    id: Guid.generate(),
    createdDate: new AHDate(),
    numUnreadMessages: 0,
    isResolved: false,
  });

  public static fromJSON(json: CommentThreadJSON | null | undefined): CommentThread | null | undefined {
    if (!json) return json;
    const parsed = CommentThreadSchema.deserialize(json);
    return new CommentThread(parsed);
  }

  public static fromProposalField(params: {
    proposal: Proposal;
    field: ProposalField;
    isViewingAsVendor: boolean;
    isExternal?: boolean;
  }): CommentThread {
    const { proposal, field, isViewingAsVendor } = params;
    const isExternal = params.isExternal ?? false;
    if (!proposal.id) {
      throw new InvalidCommentThreadError("Proposal id is missing");
    }
    const subscribers = getSubscribersForProposal({ isExternal, proposal, isViewingAsVendor });
    return new CommentThread({ proposalId: proposal.id, field, subscribers, isExternal });
  }

  public static fromForum(forum: Forum): CommentThread {
    if (forum.topic?.entityClass?.value !== "Work.Proposal") {
      throw new InvalidCommentThreadError("Forum is not a proposal forum");
    }
    if (!forum.topic?.entityId) {
      throw new InvalidCommentThreadError("Proposal id is missing on comment forum");
    }
    if (!forum.topic?.context) {
      throw new InvalidCommentThreadError("Proposal context is missing on comment forum");
    }
    const contextJSON = JSON.parse(forum.topic.context);
    const field = ProposalField.fromJSON(contextJSON.field);
    if (!field) {
      console.warn(`Invalid proposal field ${contextJSON.field}`);
    }

    // this likes to crash using array tools like contextJSON.audience.some
    const audiences = contextJSON.audience;
    let isExternal = false;
    if (audiences) {
      for (const audience of audiences) {
        if (audience.includes("all")) {
          isExternal = true;
          break;
        }
      }
    } else {
      console.warn(`Invalid proposal audience`);
    }

    return new CommentThread({
      proposalId: forum.topic.entityId,
      field: field ?? contextJSON.field,
      subscribers: forum.subscribers ?? [],
      isExternal,
      id: forum.id,
      createdDate: forum.createdDate,
      numUnreadMessages: forum.numUnreadMessages ?? 0,
    });
  }

  public id?: Guid;
  public proposalId: Guid;
  public field: ProposalField;
  public isExternal: boolean;
  public subscribers: EntityMember[];
  public createdDate?: AHDate;
  public isResolved?: boolean;
  public numUnreadMessages?: number;

  constructor(params: {
    proposalId: Guid;
    field: ProposalField;
    subscribers?: EntityMember[];
    isExternal?: boolean;
    id?: Guid;
    createdDate?: AHDate;
    numUnreadMessages?: number;
    isResolved?: boolean;
  }) {
    this.id = params.id;
    this.proposalId = params.proposalId;
    this.field = params.field;
    this.subscribers = params.subscribers ?? [];
    this.isExternal = params.isExternal ?? false;
    this.createdDate = params.createdDate;
    this.numUnreadMessages = params.numUnreadMessages ?? 0;
    this.isResolved = params.isResolved ?? false;
  }

  public getAudience(isViewingAsVendor: boolean): Audience {
    if (this.isExternal) {
      return Audience.AllReviewers;
    }
    return isViewingAsVendor ? Audience.VendorReviewers : Audience.ClientReviewers;
  }

  public toForum(params: { isViewingAsVendor: boolean }): Forum {
    const { isViewingAsVendor } = params;
    let audience = isViewingAsVendor ? "vendorReviewers" : "clientReviewers";
    if (this.isExternal) {
      audience = "allReviewers";
    }
    return Forum.create({
      name: new ForumName(`Proposal ${this.getAudience(isViewingAsVendor)} ${this.field.toString()} comment thread`),
      topic: Topic.create({
        entityClass: new ForumEntityClass("Work.Proposal"),
        entityId: this.proposalId,
        context: JSON.stringify({
          field: this.field,
          audience: [audience],
        }),
      }),
      subscribers: this.subscribers,
      id: this.id,
      creator: undefined,
      createdDate: this.createdDate,
      ...params,
    });
  }

  public toJSON(): CommentThreadJSON {
    return CommentThreadSchema.serialize(this);
  }

  public isEqualTo(other: CommentThread): boolean {
    if (!other) return false;
    if (this.id && other.id) {
      return this.id.isEqualTo(other.id);
    } else if (
      this.proposalId.isEqualTo(other.proposalId) &&
      this.field.isEqualTo(other.field) &&
      this.isExternal === other.isExternal
    ) {
      return true;
    }
    return false;
  }
}

export class InvalidCommentThreadError extends Error {
  constructor(message: string) {
    super(message || `Invalid comment thread`);
  }
}

const getSubscribersForProposal = (params: {
  isExternal: boolean;
  proposal: Proposal;
  isViewingAsVendor: boolean;
}): EntityMember[] => {
  const { isExternal, proposal, isViewingAsVendor } = params;
  let subscribers: EntityMember[];
  if (isExternal) {
    subscribers = getAllSubscribers(proposal);
  } else if (isViewingAsVendor) {
    subscribers = getVendorSubscribers(proposal);
  } else {
    subscribers = getClientSubscribers(proposal);
  }
  subscribers = _.uniqBy(subscribers, (sub) => sub.userId.value);
  return subscribers;
};

const getClientSubscribers = (proposal: Proposal): EntityMember[] => {
  const clientSubscribers: EntityMember[] = [];
  if (Masked.isNotMasked(proposal.clientLeader) && proposal.clientLeader?.userId) {
    clientSubscribers.push(proposal.clientLeader);
  }

  clientSubscribers.push(...proposal.clientReviewers.map((reviewer) => reviewer.entityMember));
  return clientSubscribers;
};

const getVendorSubscribers = (proposal: Proposal): EntityMember[] => {
  const vendorSubscribers: EntityMember[] = [];

  if (Masked.isMasked(proposal.vendorTeam)) {
    return vendorSubscribers;
  }
  if (!proposal.vendorTeam) {
    return vendorSubscribers;
  }

  if (proposal.vendorTeam?.leader?.userId && proposal.vendorTeam.leader.legalEntityId) {
    vendorSubscribers.push(proposal.vendorTeam.leader);
  }
  vendorSubscribers.push(...proposal.vendorReviewers.map((reviewer) => reviewer.entityMember));
  return vendorSubscribers;
};

const getAllSubscribers = (proposal: Proposal): EntityMember[] => {
  const allSubscribers: EntityMember[] = [];
  if (Masked.isNotMasked(proposal.clientLeader) && proposal.clientLeader?.userId) {
    allSubscribers.push(proposal.clientLeader, ...proposal.clientReviewers.map((reviewer) => reviewer.entityMember));
  }

  if (Masked.isMasked(proposal.vendorTeam)) return allSubscribers;

  if (proposal.vendorTeam?.leader)
    allSubscribers.push(
      proposal.vendorTeam.leader,
      ...proposal.vendorReviewers.map((reviewer) => reviewer.entityMember)
    );

  return allSubscribers;
};
